import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  History,
  Plus,
  Send,
  X,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { api, ApiError, API_URL } from '@/lib/api';
import { cn } from '@/lib/utils';
import { captureException } from '@/lib/sentry';

/**
 * ИИ-агроном — full port from frontend/src/components/AIAssistant/AIAssistantFAB.tsx.
 *
 * SSE streaming через POST /api/v1/ai/chat (ReadableStream + TextDecoder),
 * события: start / text / tool / done / error. После done — refetch messages
 * чтобы получить полный список с tool_use/tool_result.
 *
 * TODO (не критично для пилота):
 *   - ProposalCard для propose_* tools (auto-generated UI confirm)
 *   - HistoryPanel — список диалогов из GET /api/v1/ai/conversations
 */

type ContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> }
  | { type: 'tool_result'; tool_use_id: string; content: string };

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: ContentBlock[];
  created_at: string;
}

const SUGGESTIONS = [
  'Какой статус посева?',
  'Структура посевных площадей',
  'Где у нас пшеница озимая?',
  'Что с полем по NDVI?',
];

export function AIAssistantFAB() {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  // Накопленный текущий ответ AI во время стрима. Рендерим как fading bubble
  // до прихода события `done` (после которого замещается полным сообщением
  // через loadMessages — там уже могут быть tool_use/tool_result блоки).
  const [streamBuf, setStreamBuf] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = bodyRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamBuf, scrollToBottom]);

  const loadMessages = useCallback(async (id: string) => {
    try {
      const rows = await api.get<ChatMessage[]>(
        `/api/v1/ai/conversations/${id}/messages`,
      );
      setMessages(rows);
    } catch (e) {
      if (e instanceof ApiError) {
        if (import.meta.env.DEV) console.error('[ai] loadMessages failed', e.detail);
        else captureException(e, { tags: { source: 'AIAssistant.loadMessages' } });
      } else {
        if (import.meta.env.DEV) console.error('[ai] loadMessages failed', e);
        else captureException(e instanceof Error ? e : new Error(String(e)), { tags: { source: 'AIAssistant.loadMessages' } });
      }
    }
  }, []);

  const newChat = () => {
    setConversationId(null);
    setMessages([]);
    setStreamBuf('');
    setHistoryOpen(false);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setStreaming(true);
    setStreamBuf('');

    // Оптимистично показываем пользовательское сообщение
    const tempUser: ChatMessage = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: [{ type: 'text', text }],
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUser]);

    try {
      const resp = await fetch(`${API_URL}/api/v1/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conversation_id: conversationId,
          message: text,
        }),
      });
      if (!resp.ok || !resp.body) {
        const txt = await resp.text().catch(() => '');
        if (import.meta.env.DEV) console.error('[ai] chat error', resp.status, txt);
        else captureException(new Error(`AI chat error ${resp.status}`), { tags: { source: 'AIAssistant.chat' } });
        setMessages((m) => [
          ...m,
          {
            id: `err-${Date.now()}`,
            role: 'assistant',
            content: [
              {
                type: 'text',
                text: `Ошибка AI: ${resp.status}. Попробуйте ещё раз.`,
              },
            ],
            created_at: new Date().toISOString(),
          },
        ]);
        setStreaming(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let gotConvId: string | null = conversationId;
      let currentText = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE events разделены двойным переводом строки
        let idx: number;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const lines = raw.split('\n');
          let eventName = 'message';
          let dataStr = '';
          for (const ln of lines) {
            if (ln.startsWith('event: ')) eventName = ln.slice(7).trim();
            else if (ln.startsWith('data: ')) dataStr = ln.slice(6);
          }
          let data: unknown;
          try {
            data = JSON.parse(dataStr);
          } catch {
            data = dataStr;
          }
          if (eventName === 'start') {
            const d = data as { conversation_id: string };
            gotConvId = d.conversation_id;
            if (!conversationId) setConversationId(d.conversation_id);
          } else if (eventName === 'text') {
            const d = data as { chunk: string };
            currentText += d.chunk;
            setStreamBuf(currentText);
          } else if (eventName === 'tool') {
            // Показываем badge "используем tool X" — будет заменено
            // в финальном loadMessages где tool_use/tool_result корректно структурированы.
            const d = data as { name: string };
            setMessages((m) => [
              ...m,
              {
                id: `tool-${Date.now()}-${Math.random()}`,
                role: 'assistant',
                content: [{ type: 'text', text: `🔧 ${d.name}…` }],
                created_at: new Date().toISOString(),
              },
            ]);
          } else if (eventName === 'done' || eventName === 'error') {
            // Достаём финальные сообщения с сервера (там tool_use/tool_result корректно)
            if (gotConvId) await loadMessages(gotConvId);
            setStreamBuf('');
            setStreaming(false);
          }
        }
      }
    } catch (e) {
      if (import.meta.env.DEV) console.error('[ai] stream failed', e);
      else captureException(e instanceof Error ? e : new Error(String(e)), { tags: { source: 'AIAssistant.stream' } });
      setMessages((m) => [
        ...m,
        {
          id: `err-${Date.now()}`,
          role: 'assistant',
          content: [
            {
              type: 'text',
              text: 'Не удалось получить ответ AI. Проверьте соединение и попробуйте ещё раз.',
            },
          ],
          created_at: new Date().toISOString(),
        },
      ]);
      setStreamBuf('');
      setStreaming(false);
    }
  };

  // Минимальный markdown — только bold/italic/code/newlines (как в frontend/).
  const renderMarkdown = (text: string): React.ReactNode => {
    const lines = text.split('\n');
    const parseInline = (s: string, keyPrefix: string): React.ReactNode[] => {
      const parts: React.ReactNode[] = [];
      const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
      let last = 0;
      let m: RegExpExecArray | null;
      let i = 0;
      while ((m = re.exec(s)) !== null) {
        if (m.index > last) parts.push(s.slice(last, m.index));
        const tok = m[0];
        const key = `${keyPrefix}-${i++}`;
        if (tok.startsWith('**') && tok.endsWith('**')) {
          parts.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
        } else if (tok.startsWith('*') && tok.endsWith('*')) {
          parts.push(<em key={key}>{tok.slice(1, -1)}</em>);
        } else if (tok.startsWith('`') && tok.endsWith('`')) {
          parts.push(
            <code
              key={key}
              className="rounded bg-muted px-1 py-0.5 text-[0.92em]"
            >
              {tok.slice(1, -1)}
            </code>,
          );
        }
        last = m.index + tok.length;
      }
      if (last < s.length) parts.push(s.slice(last));
      return parts;
    };
    return lines.map((line, i) => (
      <span key={`l${i}`}>
        {parseInline(line, `l${i}`)}
        {i < lines.length - 1 && <br />}
      </span>
    ));
  };

  const renderMessage = (m: ChatMessage) => {
    const texts = m.content.filter((c) => c.type === 'text') as Extract<
      ContentBlock,
      { type: 'text' }
    >[];
    const combined = texts.map((t) => t.text).join('\n');
    if (!combined.trim()) return null;
    const isUser = m.role === 'user';
    return (
      <div
        key={m.id}
        className={cn(
          'mb-3 flex items-start gap-2',
          isUser && 'flex-row-reverse',
        )}
      >
        <div
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
            isUser
              ? 'bg-secondary text-secondary-foreground'
              : 'bg-primary text-primary-foreground',
          )}
        >
          {isUser ? 'Я' : 'ИИ'}
        </div>
        <div
          className={cn(
            'max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary text-foreground',
          )}
        >
          {isUser ? combined : renderMarkdown(combined)}
        </div>
      </div>
    );
  };

  const fabVisible = !open && !historyOpen;
  const showWelcome = useMemo(() => messages.length === 0, [messages]);

  return (
    <>
      {fabVisible && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Открыть ИИ-агроном"
          title="ИИ-агроном"
          // AK-374 — отступ от низа/правого края с учётом safe-area, чтобы
          // ФАБ не залезал под home-indicator / нижнюю адресную строку iOS.
          style={{
            bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
            right: 'calc(1.5rem + env(safe-area-inset-right, 0px))',
          }}
          className={cn(
            'fixed z-40 flex h-14 w-14 items-center justify-center rounded-full',
            'bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all',
            'text-base font-bold tracking-wide',
          )}
        >
          ИИ
        </button>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-[440px] sm:max-w-[440px] p-0 flex flex-col"
          hideClose
        >
          <SheetTitle className="sr-only">ИИ-агроном</SheetTitle>
          <SheetDescription className="sr-only">
            Чат с ИИ-агрономом по полям, культурам и NDVI
          </SheetDescription>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                ИИ
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold">ИИ-агроном</span>
                <span className="text-[11px] text-muted-foreground">
                  помощник по полям и культурам
                </span>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setHistoryOpen(true)}
                title="История"
              >
                <History className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={newChat}
                title="Новый чат"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                title="Закрыть"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-4">
            {showWelcome ? (
              <div className="flex flex-col gap-3">
                <div className="text-base font-semibold">Привет!</div>
                <div className="text-sm text-muted-foreground">
                  Расскажу про поля, культуры, NDVI и структуру посевов по
                  активному scope.
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SUGGESTIONS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setInput(q)}
                      className="rounded-full border border-border bg-card px-3 py-1.5 text-xs hover:bg-accent transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map(renderMessage)
            )}
            {streaming && streamBuf && (
              <div className="mb-3 flex items-start gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                  ИИ
                </div>
                <div className="max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed bg-secondary text-foreground">
                  {renderMarkdown(streamBuf)}
                </div>
              </div>
            )}
            {streaming && !streamBuf && (
              <div className="flex items-center gap-2 mt-1">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                  ИИ
                </div>
                <div className="flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-pulse" />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-pulse [animation-delay:120ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-pulse [animation-delay:240ms]" />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border p-3 flex items-end gap-2">
            <textarea
              className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Задайте вопрос…"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              disabled={streaming}
            />
            <Button
              onClick={() => void send()}
              disabled={streaming || !input.trim()}
              size="icon"
              aria-label="Отправить"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* TODO: HistoryPanel — отдельный Sheet с GET /api/v1/ai/conversations */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent
          side="right"
          className="w-[440px] sm:max-w-[440px] p-0 flex flex-col"
          hideClose
        >
          <SheetTitle className="sr-only">История чатов</SheetTitle>
          <SheetDescription className="sr-only">
            Список предыдущих диалогов с ИИ-агрономом
          </SheetDescription>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="text-sm font-semibold">История чатов</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setHistoryOpen(false)}
              aria-label="Закрыть"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="p-4 text-sm text-muted-foreground">
            TODO: список диалогов из <code>/api/v1/ai/conversations</code>.
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default AIAssistantFAB;
